<template>
  <!--
    Full-page wrapper around PrivateChatModal. Used on mobile so the
    1-on-1 chat opens as its own navigable screen (with a real back
    button via the browser/router history) instead of as a popup
    floating over the participant card or live-stream side panel.

    Desktop still uses the modal form (see openPrivateChatWith /
    showPrivateChatModal in LiveStream / Navbar / LobbyParticipants).
  -->
  <div class="pcm-page" :data-theme="themeAttr">
    <PrivateChatModal :recipient="recipient" @close="onClose" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PrivateChatModal from '@/components/PrivateChatModal';

export default {
  name: 'PrivateChatPage',
  components: { PrivateChatModal },
  computed: {
    ...mapGetters('Authentication', { profile: 'getUser' }),
    routeUserId() {
      return this.$route.params.userId || this.$route.query.userId || null;
    },
    allUsers() {
      const items =
        (this.$store.state.Users && this.$store.state.Users.items) || [];
      if (Array.isArray(items)) return items;
      return Object.values(items);
    },
    // Resolve the recipient from the route param against the Users
    // store. Returns null when there's no match — PrivateChatModal
    // falls back to its conversation-list picker in that case.
    recipient() {
      if (!this.routeUserId) return null;
      const id = String(this.routeUserId);
      const match = this.allUsers.find((u) => u && String(u.UserID) === id);
      return match || null;
    },
    themeAttr() {
      // Mirror the body data-theme so the page background follows
      // the rest of the platform's dark/light mode without flashing
      // white during the route transition.
      try {
        return document.body.getAttribute('data-theme') || 'light';
      } catch (e) {
        return 'light';
      }
    }
  },
  mounted() {
    document.body.classList.add('pcm-page-open');
  },
  beforeDestroy() {
    document.body.classList.remove('pcm-page-open');
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    onClose() {
      // Prefer browser history so the user lands back where they
      // came from (live-stream, lobby, navbar inbox). If there's no
      // history entry (e.g. deep-linked), fall back to /live-stream.
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace({ name: 'live-stream' });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pcm-page {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  height: 100dvh; // honour mobile browser chrome
  background-color: #ffffff;
  z-index: 50;
  overflow: hidden;

  // The modal markup uses 100% width/height of its host — make sure
  // the wrapper fills the viewport.
  ::v-deep .pcm {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}

// Dark-mode background so the page doesn't flash white before the
// modal styles paint over it.
.pcm-page[data-theme='dark'] {
  background-color: #0a0e15;
}
</style>
